/**
 * Hooks and HOC for tool items (separate from ToolItemsContext for Fast Refresh).
 * Import useToolMode, useShapedItemsForTools, useToolItems, withToolItems from here.
 * Uses createElement in withToolItems so this file can stay .ts (no JSX).
 */
import { createElement, useContext, useMemo, type ComponentType, type FC } from 'react';
import { ToolItemsContext, ToolItemsProvider, type ToolHostContext, type ToolSituationalContext } from './ToolItemsContext';
import { useItemsStore } from '../stores/prioritization/itemsStore';
import type { ToolItem } from './ToolItemsContext';
import { readToolSettings, type ToolSettings } from '../lib/toolSettings';

export type { ToolHostContext, ToolItem, ToolSituationalContext };

export function useToolItems() {
  return useContext(ToolItemsContext);
}

/**
 * Returns the full situational context for the current tool invocation.
 * Prefer this over the coarse `useToolHostContext` for UI decisions.
 */
export function useToolSituationalContext(): ToolSituationalContext | undefined {
  return useContext(ToolItemsContext)?.situationalContext;
}

/**
 * The settings the facilitator chose for this tool when the session was set
 * up (dots per person, budget share, Pareto band), with defaults when the
 * tool runs outside a session or nothing was chosen.
 */
export function useToolSettings(): ToolSettings {
  const toolConfig = useContext(ToolItemsContext)?.situationalContext?.toolConfig;
  return useMemo(() => readToolSettings(toolConfig), [toolConfig]);
}

export type ToolMode = 'app' | 'standalone';

export function useToolHostContext(): ToolHostContext {
  const toolItemsContext = useContext(ToolItemsContext);
  const items = useItemsStore(state => state.items);
  const hasShapedItems = useMemo(
    () => items.some(c => c.lifecycle_state === 'item'),
    [items]
  );

  if (toolItemsContext?.hostContext) {
    return toolItemsContext.hostContext;
  }

  return hasShapedItems ? 'workspace' : 'standalone';
}

export function useToolMode(): ToolMode {
  const hostContext = useToolHostContext();
  return hostContext === 'standalone' ? 'standalone' : 'app';
}

export function useShapedItemsForTools(): ToolItem[] {
  const items = useItemsStore(state => state.items);
  return useMemo(() => {
    const shapedItems = items.filter(item => item.lifecycle_state === 'item');
    return shapedItems.map(item => {
      let attributesObj: Record<string, unknown> = {};
      if (item.attributes) {
        if (Array.isArray(item.attributes)) {
          for (const av of item.attributes) {
            if (av && av.attributeKey !== undefined) {
              attributesObj[av.attributeKey] = av.value;
            }
          }
        } else {
          attributesObj = item.attributes as Record<string, unknown>;
        }
      }
      return {
        id: item.id,
        name: item.title,
        description: item.description || '',
        attributes: attributesObj,
        sourceId: item.id,
        sourceType: item.lifecycle_state as 'item' | 'priority',
      };
    });
  }, [items]);
}

export function withToolItems<P extends object>(
  WrappedComponent: ComponentType<P>
): FC<P> {
  return function WithToolItemsWrapper(props: P) {
    const shapedItems = useShapedItemsForTools();
    return createElement(
      ToolItemsProvider,
      { items: shapedItems, hostContext: 'workspace' },
      createElement(WrappedComponent, props)
    );
  };
}
